/**
 * One word for where an account is going (docs/engineering/admin-customer-insights.md §3.1),
 * computed in this one place so the customer page and the fleet overview never disagree.
 * The thresholds are Dustin's to tune (decision 3).
 */
import type { CustomerVolume } from '../types';
import { windowSum } from './volume-math';

/** The last 30 days at least this much over (or under) the 30 before is growing (or slowing). */
export const HEALTH_CHANGE = 0.15;
/** No pages for this many days, after having some, is quiet. */
export const QUIET_DAYS = 14;
/** First pages within this many days is new. */
export const NEW_DAYS = 30;

export type HealthWord = 'none yet' | 'new' | 'quiet' | 'growing' | 'slowing' | 'steady';
export type HealthTone = 'success' | 'warning' | 'danger' | 'neutral';

export interface Health {
  word: HealthWord;
  /** A glyph beside the word, so the badge never speaks by colour alone. */
  icon: string;
  tone: HealthTone;
  /** The sentence behind the word, for its title and the help line. */
  why: string;
}

const DAY_MS = 86_400_000;

function daysSince(iso: string, today: string): number {
  return Math.floor((Date.parse(`${today}T23:59:59Z`) - Date.parse(iso)) / DAY_MS);
}

/** What health is read from: the two 30-day windows and the first and last pages. */
export interface HealthInput {
  /** yyyy-MM-dd, the day the windows end. */
  today: string;
  firstPagesAt: string | null;
  lastPagesAt: string | null;
  last30: number;
  prior30: number;
}

/** The account's health from its volume; needs 60 days of `days` for the comparison. */
export function customerHealth(v: CustomerVolume): Health {
  return healthOf({
    today: v.today,
    firstPagesAt: v.firstPagesAt,
    lastPagesAt: v.lastPagesAt,
    last30: windowSum(v.days, 30),
    prior30: windowSum(v.days, 30, 30),
  });
}

/** The health word from the two windows: the one rule the customer page and the overview share. */
export function healthOf(v: HealthInput): Health {
  if (!v.firstPagesAt || !v.lastPagesAt) {
    return { word: 'none yet', icon: '·', tone: 'neutral', why: 'No pages have arrived yet.' };
  }
  const idle = daysSince(v.lastPagesAt, v.today);
  if (idle >= QUIET_DAYS) {
    return { word: 'quiet', icon: '⏸', tone: 'danger', why: `No pages for ${idle} days.` };
  }
  if (daysSince(v.firstPagesAt, v.today) < NEW_DAYS) {
    return {
      word: 'new',
      icon: '✦',
      tone: 'neutral',
      why: `First pages under ${NEW_DAYS} days ago.`,
    };
  }
  return trend(v);
}

/** Growing, slowing or steady: the last 30 days against the 30 before. */
function trend(v: HealthInput): Health {
  const now = v.last30;
  const before = v.prior30;
  const change = before === 0 ? (now > 0 ? 1 : 0) : (now - before) / before;
  const rounded = Math.round(change * 100);
  const pct = `${Math.abs(rounded)}%`;
  // Judged on the whole percent the screen shows, so "+15%" beside the number is never "steady".
  if (rounded >= HEALTH_CHANGE * 100) {
    return {
      word: 'growing',
      icon: '↗',
      tone: 'success',
      why: `Last 30 days ${pct} over the 30 before.`,
    };
  }
  if (rounded <= -HEALTH_CHANGE * 100) {
    return {
      word: 'slowing',
      icon: '↘',
      tone: 'warning',
      why: `Last 30 days ${pct} under the 30 before.`,
    };
  }
  return {
    word: 'steady',
    icon: '→',
    tone: 'neutral',
    why: `Last 30 days within ${pct} of the 30 before.`,
  };
}
